/*
  FDS · Header navigation (Phase 47)
  Desktop mega nav: hover intent, click, keyboard, scrim.
  Mobile drawer: level switching inside Broadcast's header-drawer.
  No dependencies. Loaded deferred from sections/header.liquid when the
  FDS nav setting is on. Everything degrades to plain links without JS.
*/
(function () {
  'use strict';

  var OPEN_DELAY = 120;
  var CLOSE_DELAY = 180;

  /* ---------- Desktop mega nav ---------- */
  function MegaNav(nav) {
    this.nav = nav;
    this.wrapper = nav.closest('[data-header-wrapper]');
    this.items = Array.prototype.slice.call(nav.querySelectorAll('[data-fds-item]'));
    this.triggers = this.items.map(function (item) { return item.querySelector('[data-fds-trigger]'); });
    this.topLinks = Array.prototype.slice.call(nav.querySelectorAll('.fds-nav__list > .fds-nav__item > .fds-nav__trigger, .fds-nav__list > .fds-nav__item > .fds-nav__link'));
    this.scrim = nav.querySelector('[data-fds-scrim]');
    this.openItem = null;
    this.timer = null;
    this.bind();
  }

  MegaNav.prototype.bind = function () {
    var self = this;

    this.items.forEach(function (item) {
      var trigger = item.querySelector('[data-fds-trigger]');
      var panel = item.querySelector('[data-fds-panel]');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (self.openItem === item) {
          /* A click on a panel that hover already opened keeps it open
             (a second click closes). Click-opened panels toggle. */
          if (self.openedByHover) { self.openedByHover = false; return; }
          self.close();
        } else {
          self.open(item);
        }
      });

      /* Hover intent, mouse only. Touch and pen use the click above. */
      item.addEventListener('pointerenter', function (e) {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        self.clearTimer();
        self.timer = setTimeout(function () { self.open(item, true); }, OPEN_DELAY);
      });
      item.addEventListener('pointerleave', function (e) {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        self.clearTimer();
        self.timer = setTimeout(function () {
          if (self.openItem === item) self.close();
        }, CLOSE_DELAY);
      });
    });

    this.nav.addEventListener('keydown', function (e) { self.onKeydown(e); });
    this.nav.addEventListener('focusout', function (e) {
      if (!self.openItem) return;
      var next = e.relatedTarget;
      if (next && self.nav.contains(next)) return;
      self.close();
    });

    if (this.scrim) {
      this.scrim.addEventListener('click', function () { self.close(); });
    }

    window.addEventListener('scroll', function () {
      if (self.openItem) self.close();
    }, { passive: true });

    document.addEventListener('theme:cart-drawer:show', function () { self.close(); });
    document.addEventListener('theme:resize:width', function () { self.close(); });
  };

  MegaNav.prototype.clearTimer = function () {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
  };

  MegaNav.prototype.open = function (item, byHover) {
    this.clearTimer();
    if (this.openItem && this.openItem !== item) this.close(true);
    if (this.openItem === item) return;
    this.openedByHover = !!byHover;
    var trigger = item.querySelector('[data-fds-trigger]');
    var panel = item.querySelector('[data-fds-panel]');
    trigger.setAttribute('aria-expanded', 'true');
    panel.classList.add('is-open');
    this.openItem = item;
    if (this.scrim) {
      this.scrim.hidden = false;
      /* next frame so the opacity transition runs */
      var scrim = this.scrim;
      requestAnimationFrame(function () { scrim.classList.add('is-visible'); });
    }
    if (this.wrapper) this.wrapper.classList.add('fds-nav-open', 'meganav--visible');
    document.body.classList.add('fds-nav-open');
  };

  MegaNav.prototype.close = function (keepScrim) {
    this.clearTimer();
    if (!this.openItem) return;
    var item = this.openItem;
    var trigger = item.querySelector('[data-fds-trigger]');
    var panel = item.querySelector('[data-fds-panel]');
    trigger.setAttribute('aria-expanded', 'false');
    panel.classList.remove('is-open');
    this.openItem = null;
    this.openedByHover = false;
    if (keepScrim) return;
    if (this.scrim) {
      var scrim = this.scrim;
      scrim.classList.remove('is-visible');
      setTimeout(function () { if (!scrim.classList.contains('is-visible')) scrim.hidden = true; }, 220);
    }
    if (this.wrapper) this.wrapper.classList.remove('fds-nav-open', 'meganav--visible');
    document.body.classList.remove('fds-nav-open');
  };

  MegaNav.prototype.onKeydown = function (e) {
    var key = e.key;
    if (key === 'Escape' || key === 'Esc') {
      if (!this.openItem) return;
      var trigger = this.openItem.querySelector('[data-fds-trigger]');
      this.close();
      trigger.focus();
      e.preventDefault();
      return;
    }
    var idx = this.topLinks.indexOf(document.activeElement);
    if (idx === -1) return;
    var next = null;
    if (key === 'ArrowRight') next = this.topLinks[(idx + 1) % this.topLinks.length];
    else if (key === 'ArrowLeft') next = this.topLinks[(idx - 1 + this.topLinks.length) % this.topLinks.length];
    else if (key === 'Home') next = this.topLinks[0];
    else if (key === 'End') next = this.topLinks[this.topLinks.length - 1];
    else if (key === 'ArrowDown' && this.openItem && this.openItem.contains(document.activeElement)) {
      var first = this.openItem.querySelector('[data-fds-panel] a');
      if (first) { first.focus(); e.preventDefault(); }
      return;
    }
    if (next) {
      e.preventDefault();
      if (this.openItem) this.close();
      next.focus();
    }
  };

  /* ---------- Mobile drawer levels ---------- */
  function DrawerNav(drawer) {
    this.drawer = drawer;
    this.scroll = drawer.querySelector('[data-fds-mnav-scroll]');
    this.root = drawer.querySelector('[data-fds-level="root"]');
    this.levels = Array.prototype.slice.call(drawer.querySelectorAll('[data-fds-level]:not([data-fds-level="root"])'));
    this.lastTrigger = null;
    this.bind();
  }

  DrawerNav.prototype.bind = function () {
    var self = this;
    this.drawer.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-fds-open]');
      if (opener && self.drawer.contains(opener)) {
        e.preventDefault();
        self.showLevel(opener.getAttribute('data-fds-open'), opener);
        return;
      }
      var back = e.target.closest('[data-fds-back]');
      if (back && self.drawer.contains(back)) {
        e.preventDefault();
        self.showRoot();
      }
    });
    /* Broadcast fires this after the drawer has closed. Reset so the next
       open starts on the first screen. Also reset on open as a backstop. */
    document.addEventListener('theme:sliderule:close', function () { self.showRoot(false); });
    this.drawer.addEventListener('theme:drawer:open', function () { self.showRoot(false); });
  };

  DrawerNav.prototype.showLevel = function (id, trigger) {
    var level = document.getElementById(id);
    if (!level) return;
    this.levels.forEach(function (l) { l.hidden = true; l.classList.remove('is-active'); });
    this.root.hidden = true;
    this.root.classList.remove('is-active');
    level.hidden = false;
    level.classList.add('is-active');
    if (trigger) { trigger.setAttribute('aria-expanded', 'true'); this.lastTrigger = trigger; }
    this.scrollTop();
    var back = level.querySelector('[data-fds-back]');
    if (back) back.focus({ preventScroll: true });
  };

  DrawerNav.prototype.showRoot = function (focusTrigger) {
    var wasOpen = this.levels.some(function (l) { return !l.hidden; });
    this.levels.forEach(function (l) { l.hidden = true; l.classList.remove('is-active'); });
    this.root.hidden = false;
    this.root.classList.add('is-active');
    this.drawer.querySelectorAll('[data-fds-open][aria-expanded="true"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    if (!wasOpen) return;
    this.scrollTop();
    if (focusTrigger !== false && this.lastTrigger) this.lastTrigger.focus({ preventScroll: true });
  };

  DrawerNav.prototype.scrollTop = function () {
    var body = this.drawer.querySelector('.drawer__body');
    if (body) body.scrollTop = 0;
    if (this.scroll) this.scroll.scrollTop = 0;
  };

  /* ---------- Init ---------- */
  function init() {
    document.querySelectorAll('[data-fds-nav]').forEach(function (nav) {
      if (!nav.fdsMegaNav) nav.fdsMegaNav = new MegaNav(nav);
    });
    document.querySelectorAll('[data-fds-mnav]').forEach(function (drawer) {
      if (!drawer.fdsDrawerNav) drawer.fdsDrawerNav = new DrawerNav(drawer);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  /* Theme editor re-renders the header section */
  document.addEventListener('shopify:section:load', init);
})();
